import { useEffect, useState } from 'react'

import { MoreVert } from 'assets/Icons'
import {
  ButtonPopover,
  DropdownItem,
  LoadingIndicator,
  Typography,
} from 'components/common'
import { t } from 'i18next'
import { ChevronsUp, Download } from 'react-feather'
import { useNavigate } from 'react-router-dom'

import styles from './MaterialsMorePopover.module.scss'
import { OfficeName } from '../../../../../constants/application-constants'
import { useFrontOfficeCheck } from '../../../../../hooks'
import { GetDownloadUrlRequest } from '../../../Documents/api/documentApi.types'
import { filesApi } from '../../../Other/api'
import MaterialChangeNameModal from '../MaterialChangeNameModal/MaterialChangeNameModal'
import MaterialDeleteModal from '../MaterialDeleteModal/MaterialDeleteModal'

interface MaterialMorePopoverProps {
  materialId: string
  fileId: string
  materialType?: string
  className?: string
  styleInverted?: boolean
  onMaterialDelete?: () => void
  onMaterialRename?: (newName: string) => void
  guidelineId?: string
}

const MaterialMorePopover = ({
  materialId,
  fileId,
  className = '',
  styleInverted = false,
  onMaterialDelete = () => {},
  onMaterialRename = () => {},
  guidelineId,
}: MaterialMorePopoverProps) => {
  const navigate = useNavigate()
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const isFrontOffice = useFrontOfficeCheck()
  const [triggerGetDownloadUrl] = filesApi.useLazyGetFileDownloadUrlByIdQuery()

  const togglePopover = () => {
    setPopoverOpen((prev) => !prev)
  }

  const handleMaterialPromoteClick = () => {
    navigate(
      `/${OfficeName.BACK}/practices/${guidelineId}/promote-material?mid=${materialId}`
    )
  }

  const isDownloadDisabled = !(downloadUrl !== null && downloadUrl.length > 0)

  /**
   * Refresh every time the popover is opened to get the newest signed URL
   */
  useEffect(() => {
    setDownloadUrl(null)

    const props: GetDownloadUrlRequest = {
      id: fileId!,
    }

    if (popoverOpen) {
      triggerGetDownloadUrl(props, false).then((result) => {
        setDownloadUrl(result.data?.data.file.url ?? null)
      })
    }
  }, [popoverOpen, materialId, triggerGetDownloadUrl, isFrontOffice, fileId])

  return (
    <ButtonPopover
      id={`view-document-${materialId}`}
      buttonProps={{
        color: styleInverted ? 'dark' : 'default',
        size: 'x-small',
        variant: styleInverted ? 'secondary' : 'subtle',
        icon: <MoreVert />,
        onClick: togglePopover,
      }}
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      transformOrigin={{
        horizontal: 'center',
        vertical: 'top',
      }}
      open={popoverOpen}
      updateOpen={togglePopover}
      rootClassName={className}
      buttonLabel={`${t('document.options')}`}
    >
      <a
        href={downloadUrl ?? ''}
        target="_blank"
        onClick={togglePopover}
        rel="noreferrer"
        className={styles.link}
      >
        <DropdownItem
          icon={
            isDownloadDisabled ? (
              <LoadingIndicator size="x-small" />
            ) : (
              <Download />
            )
          }
          disabled={isDownloadDisabled}
        >
          <Typography>{t('document.popoverDownload')}</Typography>
        </DropdownItem>
      </a>

      <MaterialChangeNameModal
        materialId={materialId}
        togglePopover={togglePopover}
        onMaterialRename={onMaterialRename}
      />

      <DropdownItem
        className={styles.link}
        icon={<ChevronsUp />}
        onClick={handleMaterialPromoteClick}
      >
        <Typography>{t('materials.popover.convertToDocument')}</Typography>
      </DropdownItem>

      <MaterialDeleteModal
        materialId={materialId}
        togglePopover={togglePopover}
        onMaterialDelete={onMaterialDelete}
      />
    </ButtonPopover>
  )
}

export default MaterialMorePopover
